import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Map as MapIcon, Clock } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { workshopUrl, mapImageUrl, KZ_MODES } from '../../../lib/cs2kz';
import { formatInZone, formatLocalTime, formatDuration } from '../../../lib/kzTime';
import {
  getConfig,
  eventDateFor,
  getOrCreateWeek,
  getMyMotwPlayer,
  listMotwPlayers,
} from '../../../database/mapOfTheWeek';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import SEO from '../../SEO';
import LinkKzProfile from './LinkKzProfile';
import MotwLeaderboard from './MotwLeaderboard';

const titleCase = (value) =>
  typeof value === 'string'
    ? value.replace(/(^|[-_\s])(\w)/g, (_, s, c) => c.toUpperCase())
    : value;

const sessionState = (session, now) => {
  if (now < session.startsAt) return 'upcoming';
  if (now <= session.endsAt) return 'open';
  return 'closed';
};

// A window's status, its wall-clock time in the schedule's zone, and what it
// works out to for the person reading the page.
const SessionRow = ({ session, timeZone, now }) => {
  const state = sessionState(session, now);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3',
        state === 'open' && 'border-success/50 bg-success/5'
      )}
    >
      <div className="flex items-center gap-3">
        <Clock
          className={cn('h-4 w-4', state === 'open' ? 'text-success' : 'text-muted-foreground')}
        />
        <div>
          <div className="text-sm font-medium">{session.label}</div>
          <div className="text-xs text-muted-foreground">
            {formatInZone(session.startsAt, timeZone)}–
            {formatInZone(session.endsAt, timeZone, { weekday: undefined })} ({timeZone})
            <span className="mx-1.5">·</span>
            {formatLocalTime(session.startsAt)}–{formatLocalTime(session.endsAt)} your time
          </div>
        </div>
      </div>
      {state === 'open' && (
        <Badge className="bg-success text-background">
          Open · {formatDuration(session.endsAt - now)} left
        </Badge>
      )}
      {state === 'upcoming' && (
        <Badge variant="outline">Starts in {formatDuration(session.startsAt - now)}</Badge>
      )}
      {state === 'closed' && <Badge variant="secondary">Closed</Badge>}
    </div>
  );
};

const MapOfTheWeek = () => {
  const { currentUser, userProfile } = useAuth();
  const [config, setConfig] = useState(null);
  const [week, setWeek] = useState(null);
  const [players, setPlayers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Not every course has a community screenshot; hide the frame if it 404s.
  const [imageFailed, setImageFailed] = useState(false);
  // Drives the countdowns and flips a window from "upcoming" to "open".
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const settings = await getConfig();
      const eventDate = eventDateFor(settings);
      const [weekDoc, playerList, mine] = await Promise.all([
        getOrCreateWeek(settings, eventDate),
        listMotwPlayers(),
        currentUser ? getMyMotwPlayer(currentUser.uid) : Promise.resolve(null),
      ]);
      setConfig(settings);
      setWeek(weekDoc);
      setPlayers(playerList);
      setMe(mine);
      setImageFailed(false);
    } catch (err) {
      console.error('Error loading Map of the Week:', err);
      setError('Could not load this week’s course. The CS2KZ API may be unavailable.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          <div className="h-40 animate-pulse rounded-md border bg-muted/40" />
          <div className="h-24 animate-pulse rounded-md border bg-muted/40" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="space-y-4 rounded-md border border-destructive/40 bg-destructive/5 px-6 py-10 text-center">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" onClick={load}>
            Try again
          </Button>
        </div>
      );
    }

    if (!config?.enabled) {
      return (
        <div className="rounded-md border border-dashed px-6 py-16 text-center text-muted-foreground">
          Map of the Week is paused right now. Check back soon.
        </div>
      );
    }

    if (!week) {
      return (
        <div className="rounded-md border border-dashed px-6 py-16 text-center text-muted-foreground">
          No eligible course could be selected this week. Check back soon.
        </div>
      );
    }

    const imageUrl = mapImageUrl(week.mapName, week.courseIndex);

    return (
      <div className="space-y-6">
        <Card className="overflow-hidden">
          {imageUrl && !imageFailed && (
            <img
              src={imageUrl}
              alt={`${week.mapName} — ${week.courseName}`}
              loading="lazy"
              onError={() => setImageFailed(true)}
              className="aspect-video w-full border-b object-cover"
            />
          )}
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="font-mono text-xl">{week.mapName}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Course: <span className="font-medium text-foreground">{week.courseName}</span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {KZ_MODES.map((mode) => {
                  const info = week.modes?.[mode.id];
                  if (!info?.ranked) return null;
                  return (
                    <Badge key={mode.id} variant="secondary">
                      {mode.short}
                      {info.tier ? ` · ${titleCase(info.tier)}` : ''}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {(week.sessions || []).map((session) => (
                <SessionRow
                  key={session.key}
                  session={session}
                  timeZone={config.timeZone}
                  now={now}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Two windows, one course. You compete in the first window you set a time in — runs in
              the other one don&rsquo;t count.
            </p>
            <Button asChild variant="outline" size="sm">
              <a href={workshopUrl(week.workshopId)} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                View on Steam Workshop
              </a>
            </Button>
          </CardContent>
        </Card>

        {currentUser && (
          <LinkKzProfile
            uid={currentUser.uid}
            username={userProfile?.username}
            steamLink={userProfile?.steamLink}
            me={me}
            onChanged={load}
          />
        )}

        {KZ_MODES.map((mode) => {
          const info = week.modes?.[mode.id];
          return (
            <section key={mode.id} className="space-y-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-semibold">
                  {mode.label} <span className="text-muted-foreground">({mode.short})</span>
                </h2>
                {info?.ranked && info.tier && (
                  <Badge variant="outline">Tier: {titleCase(info.tier)}</Badge>
                )}
              </div>
              {info?.ranked ? (
                <MotwLeaderboard
                  week={week}
                  mode={mode.id}
                  players={players}
                  points={config.points}
                />
              ) : (
                <div className="rounded-md border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">
                  This course isn&rsquo;t ranked in {mode.short}, so no times can be set here.
                </div>
              )}
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <SEO
        title="Map of the Week"
        description="A new CS2KZ course every week — two one-hour windows to set your time."
        canonicalPath="/map-of-the-week"
        noindex
      />

      <header className="mb-8 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <MapIcon className="h-4 w-4" />
          Map of the Week
        </div>
        <h1 className="text-3xl font-bold tracking-tight">This week&rsquo;s KZ challenge</h1>
        <p className="text-muted-foreground">
          One CS2KZ course a week, with two short windows to run it. Link your Steam profile once,
          show up in a window, and set your time. Classic and Vanilla are scored separately, and the
          top three in each mode take the points.
        </p>
      </header>

      {renderBody()}
    </div>
  );
};

export default MapOfTheWeek;
